import { type ComponentType, type SVGProps, useEffect, useRef } from "react";

export interface ContextMenuItem {
  id: string;
  label: string;
  Icon?: ComponentType<SVGProps<SVGSVGElement>>;
  variant?: "default" | "danger";
  onSelect: () => void;
}

export interface ContextMenuProps {
  /** Optional small section title shown above the items (uppercase eyebrow). */
  title?: string;
  items: readonly ContextMenuItem[];
  /** Anchor in viewport coordinates. The menu positions itself just above. */
  screenX: number;
  screenY: number;
  onClose: () => void;
  /** Optional width override in px. Defaults to 176. */
  widthPx?: number;
  /**
   * Optional ref to the element that toggles this menu (e.g. a toolbar
   * button). Clicks inside it are NOT treated as "outside" — otherwise the
   * open-click's `mousedown` would close the menu and the following `click`
   * would immediately reopen it, so the toggle could never close it.
   */
  ignoreRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Tiny floating popover menu, used for long-press / right-click style menus on
 * canvas elements. Click-outside / Escape auto-dismiss.
 *
 * Generic — does not know about any specific entity type. Callers build the
 * items array from their own domain model.
 */
export function ContextMenu({
  title,
  items,
  screenX,
  screenY,
  onClose,
  widthPx = 176,
  ignoreRef,
}: ContextMenuProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDoc = (e: PointerEvent) => {
      if (!ref.current) return;
      const target = e.target as Node;
      // Clicks on the trigger element are handled by its own toggle.
      if (ignoreRef?.current?.contains(target)) return;
      if (!ref.current.contains(target)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // `pointerdown` in the CAPTURE phase at the window: it fires before any
    // canvas/Konva/React handler and cannot be swallowed by stopPropagation
    // downstream — outside-taps on a busy Konva stage reliably dismiss the
    // menu (plain bubble-phase mousedown/touchstart proved flaky there).
    // Attach deferred so the pointerdown that opened the menu (long-press
    // still holding, or the toggle click) can't immediately dismiss it.
    const handle = window.setTimeout(() => {
      window.addEventListener("pointerdown", onDoc, true);
    }, 0);
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(handle);
      window.removeEventListener("pointerdown", onDoc, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, ignoreRef]);

  const margin = 8;
  const left = Math.max(
    margin,
    Math.min(window.innerWidth - widthPx - margin, screenX - widthPx / 2),
  );
  const top = Math.max(margin, screenY - 96);

  return (
    <div
      ref={ref}
      role="menu"
      className="glass-floating pointer-events-auto fixed z-50 flex flex-col gap-1 rounded-2xl p-1.5 shadow-lg"
      style={{ left, top, width: widthPx }}
    >
      {title && (
        <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-muted-foreground">
          {title}
        </div>
      )}
      {items.map((item) => {
        const danger = item.variant === "danger";
        return (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            onClick={item.onSelect}
            className={
              danger
                ? "flex items-center gap-2 rounded-xl px-2 py-2 text-body-sm text-[#ffb1b8] hover:bg-[#3a1418]"
                : "flex items-center gap-2 rounded-xl px-2 py-2 text-body-sm hover:bg-white/10"
            }
          >
            {item.Icon && <item.Icon className="size-4" />}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
